import React, { useCallback, useMemo, useState } from 'react';
import classes from './style.module.scss';
import { useStoreActions, useStoreState } from '~/store';
import ConfirmationMenu from '~/app/common/ConfirmationMenu';
import Icon from '~/app/common/Icon';
import { DeleteMaterialPayload, Material } from '~/types';
import useTranslation from '~/app/hooks/useTranslation';
import { IssueStatuses } from '~/app/data/statuses/issue';
import useAccessControl from '~/app/hooks/useAccessControl';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { ServiceStatuses } from '~/app/data/statuses/services';

export interface MaterialsProps {
  type: IssueOrServiceType
}

export type ListComponent = React.FC<MaterialsProps>;

const List: ListComponent = ({ type }) => {

  const basePath = (type === IssueOrServiceType.ISSUE )? 'issue' : 'service';
  const materials = useStoreState(state => state[basePath].data.materials ?? []);
  const id = useStoreState(state => state[basePath].data.id);
  const status = useStoreState(state => state[basePath].data.status);
  const deptPrefix = useStoreState(state => state[basePath].data.departmentPrefix);
  const netWorkStatus = useStoreState(state => state.app.offline.status);

  const deleteMaterialAction = useStoreActions(actions => actions[basePath].deleteMaterial);
  const fetchMaterialsAction = useStoreActions(actions => actions[basePath].fetchMaterials);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [material, setMaterial] = useState<Material>();

  const { t } = useTranslation('material');
  const { checkPermission } = useAccessControl();

  const addMaterialPermission = deptPrefix 
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.addMatrial(deptPrefix))
    : false;

  const onDelete = useCallback(() => {
    if (!material) {
      setIsConfirmationOpen(false);
      return;
    }
    const data: DeleteMaterialPayload = {
      materialId: material.id,
      storageId: material.workOrderMaterialId,
      type: material.type
    }
    setMaterial(undefined);
    deleteMaterialAction(data).then(res => {
      if (res.success) {
        fetchMaterialsAction(id);
      }
    });
    setIsConfirmationOpen(false);
  }, [material, id, fetchMaterialsAction, deleteMaterialAction, setIsConfirmationOpen])

  const onDeleteConfirm = (materialToDelete) => {
    setMaterial(materialToDelete);
    setIsConfirmationOpen(true);
  }

  const isOpenStatus = useMemo(() => {
    if (type === IssueOrServiceType.ISSUE) {
      return status === IssueStatuses.REGISTERED;
    }
    if (type === IssueOrServiceType.SERVICE) {
      return status === ServiceStatuses.TRIGGERED
    }
    return false;
  }, [type, status]);

  return (
    <div className={classes['listContainer']}>
      {materials.map((material, index) => (
        <div className={classes['partBox']} key={index}>
          <div className={classes['row']}>
            <span className={classes['partName']}>{material.article_name}</span>
            <span className={classes['partQuantity']}>{material.quantity} {t('item')}</span>
          </div>
          <div className={classes['row']}>
            <span className={classes['partNumber']}>{t('partNo')}: {material.article_no}</span>
            {!netWorkStatus && isOpenStatus && addMaterialPermission && (
              <button className={classes['deleteButton']} onClick={() => onDeleteConfirm(material)}>
                <Icon name={'delete'} />
              </button>
            )}
          </div>
        </div>
      ))}          
      <ConfirmationMenu
        isOpen={isConfirmationOpen}
        onConfirm={onDelete}
        onCancel={() => setIsConfirmationOpen(false)}
        confirmationText={t('deleteConfirm')}
      />
    </div>
  );
};

export default List
